/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

import type { ShopperBasketsV2 } from '@/scapi';
import QuantityPicker from '@/components/quantity-picker/quantity-picker';
import { ConfirmationDialog } from '@/components/confirmation-dialog';
import { Label } from '@/components/forms/label';
import { Spinner } from '@/components/spinner';
import { useItemFetcher } from '@/hooks/use-item-fetcher';
import { useBasketUpdater } from '@/providers/basket';
import { useToast } from '@/components/toast';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { MAX_LINE_ITEM_QUANTITY } from '@/lib/cart-constants';
import { cn } from '@/lib/utils';

interface KitQuantityPickerProps {
    /**
     * UUID of the kit group's anchor component PLI. The backend resolves the
     * kit instance from this item's `c_partOfKitNumber` and scales every
     * sibling component to `apkQuantity x N`.
     */
    anchorItemId: string;
    /** Current kit instance count (N) — `c_kitGroups[].quantity`. */
    value: number;
    /** Custom className for the container. */
    className?: string;
    /** Custom className forwarded to the inner QuantityPicker. Defaults to `h-10 min-w-[96px] justify-between`. */
    pickerClassName?: string;
}

/**
 * CART-19 — kit-level quantity picker.
 *
 * A kit master is exploded into one PLI per component, so there is no single
 * PLI the standard cart quantity picker could scale. This control edits the
 * kit INSTANCE count and persists via `/action/cart-kit-quantity`, which calls
 * the `ashley-cart` `set-kit-quantity` endpoint to set every component to
 * `apkQuantity x N` atomically (legacy parity: the SFRA cart kit quantity
 * selector scaled all kit items together).
 *
 * Parity with the standard `<CartQuantityPicker>`:
 *   - Entering `0` opens a Remove-item confirmation prompt; confirming submits
 *     the standard cart remove action for the anchor PLI, which cascades to
 *     every component sharing the kit's `c_partOfKitNumber` (whole-kit remove).
 *   - While a change is being persisted the picker is locked behind a loading
 *     overlay so the shopper can't queue a second change mid-flight.
 *   - A success toast is shown once the kit quantity update is confirmed.
 */
export default function KitQuantityPicker({
    anchorItemId,
    value,
    className,
    pickerClassName,
}: KitQuantityPickerProps): ReactElement {
    const { t: tQuantity } = useTranslation('quantitySelector');
    const { t: tRemove } = useTranslation('removeItem');
    const { t: tCart } = useTranslation('cart');
    const config = useConfig<AppConfig>();
    const removeAction = config.pages.cart.removeAction;
    const updateBasket = useBasketUpdater();
    const { addToast } = useToast();

    const fetcher = useItemFetcher({ itemId: anchorItemId, componentName: 'kit-quantity-picker' });
    const isSubmitting = fetcher.state === 'submitting';

    // Optimistic local value so the stepper reflects the change immediately.
    const [quantity, setQuantity] = useState<number>(value);
    const lastConfirmedRef = useRef<number>(value);
    const pendingQuantityRef = useRef<number | null>(null);
    // Whether the Remove-item confirmation prompt (triggered by entering 0) is open.
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    // Distinguishes an in-flight kit remove from a kit quantity update so the
    // response effect can surface the correct success/failure toast.
    const isRemovingRef = useRef(false);

    // Resync from the server-confirmed kit quantity when no local change is in
    // flight (e.g. external basket updates / route revalidation).
    useEffect(() => {
        if (fetcher.state === 'idle') {
            const pendingQuantity = pendingQuantityRef.current;
            // Keep optimistic value while waiting for provider state to catch up
            // to the quantity we just submitted. This prevents the "value snaps
            // back until hard refresh" regression when server response is correct
            // but the upstream basket publish is delayed.
            if (pendingQuantity !== null && value !== pendingQuantity) {
                return;
            }
            pendingQuantityRef.current = null;
            setQuantity(value);
            lastConfirmedRef.current = value;
        }
    }, [value, fetcher.state]);

    const submitQuantity = useMemo(
        () =>
            debounce((next: number) => {
                const formData = new FormData();
                formData.append('itemId', anchorItemId);
                formData.append('quantity', String(next));
                void fetcher.submit(formData, { method: 'PATCH', action: '/action/cart-kit-quantity' });
            }, 500),
        // fetcher is stable for a given key; anchorItemId identifies this kit
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [anchorItemId]
    );

    useEffect(() => () => submitQuantity.cancel(), [submitQuantity]);

    // Apply basket updates / surface failures from the action.
    const processedRef = useRef<typeof fetcher.data>(null);
    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data || fetcher.data === processedRef.current) return;
        processedRef.current = fetcher.data;

        const wasRemoving = isRemovingRef.current;

        if (fetcher.data.success && fetcher.data.basket) {
            updateBasket(fetcher.data.basket as ShopperBasketsV2.schemas['Basket']);
            addToast(wasRemoving ? tRemove('success') : tQuantity('quantityUpdated'), 'success');
            isRemovingRef.current = false;
            return;
        }
        // Revert to the last server-confirmed value and notify.
        pendingQuantityRef.current = null;
        isRemovingRef.current = false;
        setQuantity(lastConfirmedRef.current);
        addToast(wasRemoving ? tRemove('failed') : tQuantity('quantityUpdateFailed'), 'error');
        // addToast / updateBasket are stable refs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetcher.state, fetcher.data, tQuantity, tRemove]);

    const handleChange = (_stringValue: string, numberValue: number) => {
        if (!Number.isFinite(numberValue)) return;

        // Entering 0 prompts to remove the kit (parity with <CartQuantityPicker>).
        // A kit has no single removable PLI, so the confirm handler submits the
        // standard cart remove action, which cascades the whole kit server-side.
        if (numberValue === 0) {
            submitQuantity.cancel();
            pendingQuantityRef.current = null;
            setQuantity(0);
            setShowRemoveConfirmation(true);
            return;
        }

        if (numberValue < 1 || numberValue > MAX_LINE_ITEM_QUANTITY) return;
        if (numberValue === quantity) return;
        setQuantity(numberValue);
        pendingQuantityRef.current = numberValue;
        submitQuantity.cancel();
        submitQuantity(numberValue);
    };

    // Remove-item prompt — keep the kit: restore the last server-confirmed count.
    const handleKeepItem = useCallback(() => {
        setShowRemoveConfirmation(false);
        setQuantity(lastConfirmedRef.current);
    }, []);

    // Restore the displayed count whenever the prompt is dismissed without
    // confirming (Cancel, Escape, or outside click) so it never sticks at 0.
    const handleConfirmationOpenChange = useCallback((open: boolean) => {
        setShowRemoveConfirmation(open);
        if (!open) setQuantity(lastConfirmedRef.current);
    }, []);

    // Remove-item prompt — confirm removal: submit the standard cart remove
    // action for the anchor PLI. The Ashley cascade endpoint removes every
    // component sharing the kit's `c_partOfKitNumber`, so the whole kit is
    // removed atomically (same behavior as the kit card's Remove button).
    const handleRemoveItem = useCallback(() => {
        setShowRemoveConfirmation(false);
        submitQuantity.cancel();
        pendingQuantityRef.current = null;
        isRemovingRef.current = true;
        const formData = new FormData();
        formData.append('itemId', anchorItemId);
        void fetcher.submit(formData, { method: 'POST', action: removeAction });
    }, [anchorItemId, removeAction, fetcher, submitQuantity]);

    return (
        <div className={cn('text-left md:text-right', className)}>
            <Label htmlFor="kit-quantity" className="sr-only">
                {tQuantity('quantity')}
            </Label>
            {/* Relative wrapper hosts the updating overlay so the picker is
                visually locked until the prior change is persisted. */}
            <div className="relative inline-block" aria-busy={isSubmitting}>
                <QuantityPicker
                    value={quantity.toString()}
                    onChange={handleChange}
                    min={0}
                    max={MAX_LINE_ITEM_QUANTITY}
                    disabled={isSubmitting}
                    className={pickerClassName ?? 'h-10 min-w-[96px] justify-between'}
                />
                {isSubmitting && (
                    <div
                        className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm"
                        data-testid={`sf-kit-quantity-loading-${anchorItemId}`}
                        aria-hidden>
                        <Spinner size="sm" />
                    </div>
                )}
            </div>
            {/* Remove-item confirmation prompt shown when the quantity is set to 0. */}
            <ConfirmationDialog
                open={showRemoveConfirmation}
                onOpenChange={handleConfirmationOpenChange}
                title={tRemove('confirmTitle')}
                description={tCart('removeItemConfirmDescription')}
                cancelButtonText={tRemove('keepItemButton')}
                confirmButtonText={tRemove('confirmAction')}
                onCancel={handleKeepItem}
                onConfirm={handleRemoveItem}
                confirmButtonDisabled={isSubmitting}
                cancelButtonAriaLabel={tRemove('keepItemAriaLabel')}
                confirmButtonAriaLabel={tRemove('removeItemAriaLabel')}
            />
        </div>
    );
}
